import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import './ModelToolMBS.css';

function ModelToolMBS() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [demoUrl, setDemoUrl] = useState('https://www.modelwerk.de/page/women.html');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoTriggered, setDemoTriggered] = useState(false);
  const [visibleCount, setVisibleCount] = useState(0);
  const [crawledModels, setCrawledModels] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const images = [
    { 
      src: '/img/1-dashboard.png',
      alt: "Modeltool Dashboard - Übersicht der Castings und Shootings" 
    },
    { 
      src: '/img/2-crawling.png',
      alt: "Automatisches Crawling von Model-Daten" 
    },
    { 
      src: '/img/3-modelauswahl.png',
      alt: "Model Auswahl Interface" 
    }
  ];

  // Statische Demo-Sets pro URL (Fallback, falls Crawl fehlschlägt)
  const demoPresets = useMemo(() => ({
    'https://www.modelwerk.de/page/women.html': [
      {
        type: 'model',
        name: 'AYMAN FOR AMIRI @ PFW MENSWEAR F/W 26-27',
        imageUrl: 'https://www.modelwerk.de/image/1021402.jpg?size=rhp',
        profileUrl: 'https://www.modelwerk.de/page/model-6836/Ayman.html',
        data: { hair: '', eyes: '' }
      },
      {
        type: 'model',
        name: 'MALIN RUDNICK FOR AMI @ PFW MENSWEAR F/W 26-27',
        imageUrl: 'https://www.modelwerk.de/image/1021403.jpg?size=rhp',
        profileUrl: 'https://www.modelwerk.de/page/model-8182/Malin_Rudnick.html',
        data: { hair: '', eyes: '' }
      },
      {
        type: 'model',
        name: 'CLARA HEITKAMP FOR SONGZIO @ PFW MENSWEAR F/W 26-27',
        imageUrl: 'https://www.modelwerk.de/image/1021404.jpg?size=rhp',
        profileUrl: 'https://www.modelwerk.de/page/model-7405/Clara_Heitkamp.html',
        data: { hair: '', eyes: '' }
      }
    ],
    'https://www.modelwerk.de/page/female.html': [
      {
        type: 'model',
        name: 'ADAMA JOBE',
        imageUrl: 'https://www.modelwerk.de/image/1021402.jpg?size=rhp',
        profileUrl: 'https://www.modelwerk.de/page/model/adama-jobe',
        data: { hair: 'Black', eyes: 'Brown' }
      },
      {
        type: 'model',
        name: 'ADELINE JOUAN',
        imageUrl: 'https://www.modelwerk.de/image/1021403.jpg?size=rhp',
        profileUrl: 'https://www.modelwerk.de/page/model/adeline-jouan',
        data: { hair: 'Blond', eyes: 'Blue' }
      },
      {
        type: 'model',
        name: 'ADI HAREL',
        imageUrl: 'https://www.modelwerk.de/image/1021404.jpg?size=rhp',
        profileUrl: 'https://www.modelwerk.de/page/model/adi-harel',
        data: { hair: 'Brown', eyes: 'Hazel' }
      },
      {
        type: 'model',
        name: 'ADINA FOHLIN',
        imageUrl: null,
        profileUrl: 'https://www.modelwerk.de/page/model/adina-fohlin',
        data: { hair: 'Blond', eyes: 'Blue' }
      },
      {
        type: 'model',
        name: 'ALENA GERBER',
        imageUrl: null,
        profileUrl: 'https://www.modelwerk.de/page/model/alena-gerber',
        data: { hair: 'Brown', eyes: 'Green' }
      }
    ],
    'https://select-package.agencypin.com/bundle/index/bdb1ed674ecf8f2ef316119e2838099d/51462': [
      {
        type: 'model',
        name: 'ALEX DUNSTAN',
        imageUrl: null,
        profileUrl: 'https://www.selectmodel.com/london/men/model/alex-dunstan',
        data: {
          heightCm: 188,
          chestCm: 91,
          waistCm: 81,
          hipsCm: 90,
          shoesEu: 46,
          eyes: 'Hazel',
          hair: 'Brown'
        }
      },
      {
        type: 'model',
        name: 'ALEX KING',
        imageUrl: null,
        profileUrl: 'https://www.selectmodel.com/london/men/model/alex-king',
        data: {
          heightCm: 188,
          chestCm: 101,
          waistCm: 84,
          shoesEu: 48,
          eyes: 'Hazel',
          hair: 'Brown'
        }
      },
      {
        type: 'model',
        name: 'ALEX NIANG',
        imageUrl: null,
        profileUrl: 'https://www.selectmodel.com/london/men/model/alex-niang',
        data: {
          heightCm: 188,
          chestCm: 98,
          waistCm: 80,
          hipsCm: 94,
          shoesEu: 45,
          eyes: 'Brown',
          hair: 'Black'
        }
      }
    ]
  }), []);

  const modelResults = useMemo(
    () => {
      if (!demoTriggered) return [];
      // Use crawled models if available, otherwise fall back to static presets
      const base = crawledModels.length > 0 ? crawledModels : (demoPresets[demoUrl] || []);
      return base.filter((r) => r && r.type === 'model').slice(0, visibleCount);
    },
    [demoTriggered, visibleCount, demoUrl, demoPresets, crawledModels]
  );

  // Alle Models für die Liste (nicht progressiv, sondern alle auf einmal)
  const allModels = useMemo(
    () => {
      if (!demoTriggered) return [];
      // Use crawled models if available, otherwise fall back to static presets
      const base = crawledModels.length > 0 ? crawledModels : (demoPresets[demoUrl] || []);
      return base.filter((r) => r && r.type === 'model');
    },
    [demoTriggered, demoUrl, demoPresets, crawledModels]
  );

  const runDemoCrawl = async () => {
    if (!demoUrl) return;
    setDemoLoading(true);
    setDemoTriggered(false);
    setVisibleCount(0);
    setCrawledModels([]);
    setUsingFallback(false);

    try {
      const response = await fetch(`${API_URL}/api/crawl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          url: demoUrl,
          maxDetails: 20
        })
      });

      if (!response.ok) {
        throw new Error('Crawl failed');
      }

      const data = await response.json();
      const models = Array.isArray(data) ? data.filter((item) => item && item.type === 'model') : [];
      
      setCrawledModels(models);
      
      // Start progressive display after crawl completes
      setTimeout(() => {
        setDemoTriggered(true);
        setVisibleCount(1); // Erstes Model erscheint
        setDemoLoading(false);
      }, 300);
    } catch (error) {
      console.error('Crawl error:', error);
      setUsingFallback(true);
      setTimeout(() => {
        setDemoTriggered(true);
        setVisibleCount(1);
        setDemoLoading(false);
      }, 800);
    }
  };

  const downloadModelPDF = async (model) => {
    if (!model.profileUrl) {
      alert(t.modeltool.noProfileUrl);
      return;
    }

    setDownloadingPdf(model.profileUrl);
    try {
      const response = await fetch(`${API_URL}/api/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profileUrl: model.profileUrl,
          modelName: model.name,
          modelData: model.data || {}
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'PDF-Generierung fehlgeschlagen');
      }

      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(model.name || 'model').replace(/[^a-z0-9]/gi, '_')}_profile.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download error:', error);
      alert(`${t.modeltool.pdfError} ${error.message}`);
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Weitere Models progressiv nachladen (Frontend-only Simulation)
  useEffect(() => {
    if (!demoTriggered) return;
    const total = allModels.length;
    if (visibleCount >= total) return;

    const id = setTimeout(() => {
      setVisibleCount((current) =>
        Math.min(current + 1, total)
      );
    }, 500);

    return () => clearTimeout(id);
  }, [demoTriggered, visibleCount, allModels]);

  return (
    <div className="modeltool-page">
      <button
        type="button"
        className="back-button"
        onClick={() => {
          const scrollTo = location.state && location.state.scrollTo ? location.state.scrollTo : 'projects';
          navigate('/', { state: { scrollTo } });
        }}
      >
        {t.modeltool.back}
      </button>

      <h1>Modeltool</h1>
      
      <div className="image-grid">
        {images.map((image, index) => (
          <div key={index} className="image-container">
            <img
              src={image.src}
              alt={image.alt}
              onClick={() => setSelectedImage(image)}
              className="grid-image"
            />
          </div>
        ))}
      </div>

      <div className="description-section">
        <h2>{t.modeltool.aboutTitle}</h2>
        <p>{t.modeltool.aboutText}</p>
        <ul>
          <li>{t.modeltool.feature1}</li>
          <li>{t.modeltool.feature2}</li>
          <li>{t.modeltool.feature3}</li>
          <li>{t.modeltool.feature4}</li>
        </ul>
        <p>{t.modeltool.aboutText2} {t.modeltool.offlineNote}</p>
      </div>

      <div className="tech-doc-section">
        <h2>{t.modeltool.docTitle}</h2>

        <div className="tech-doc-block">
          <h3>{t.modeltool.docTechStackTitle}</h3>
          <p>
            {t.modeltool.docTechStackText1}<strong>Next.js</strong>{t.modeltool.docTechStackText2}<strong>{t.modeltool.docTechStackNode}</strong>{t.modeltool.docTechStackText3}
          </p>
        </div>

        <div className="tech-doc-block">
          <h3>{t.modeltool.docStaticTitle}</h3>
          <p>{t.modeltool.docStaticText}</p>
        </div>

        <div className="tech-doc-block">
          <h3>{t.modeltool.docDynamicTitle}</h3>
          <p>
            {t.modeltool.docDynamicText1}<strong>Headless Browser</strong>{t.modeltool.docDynamicText2}
          </p>
        </div>

        <div className="tech-doc-block">
          <h3>{t.modeltool.docDbTitle}</h3>
          <p>{t.modeltool.docDbText}</p>
        </div>

        <div className="tech-doc-block">
          <h3>{t.modeltool.docPdfTitle}</h3>
          <p>{t.modeltool.docPdfText}</p>
        </div>
      </div>

      <section className="demo-section" aria-labelledby="crawler-demo-title">
        <div className="demo-section__header">
          <h2 id="crawler-demo-title">{t.modeltool.crawlerTitle}</h2>
          <p className="demo-privacy-note">{t.modeltool.privacyNote}</p>
        </div>

        <div className="demo-card">
          <div className="demo-card__row">
            <label className="demo-label" htmlFor="demo-url">
              URL
            </label>
            <input
              id="demo-url"
              className="demo-input"
              type="text"
              placeholder={t.modeltool.urlPlaceholder}
              value={demoUrl}
              onChange={(e) => setDemoUrl(e.target.value)}
            />
            <button
              type="button"
              className="demo-button"
              onClick={runDemoCrawl}
              disabled={!demoUrl || demoLoading}
            >
              {demoLoading ? t.modeltool.crawling : t.modeltool.crawlBtn}
            </button>
          </div>

          <div className="demo-card__row demo-card__row--results">
            {usingFallback && (
              <p className="demo-results__hint demo-results__hint--error demo-results__hint--spaced">
                {t.modeltool.fallbackWarning}
              </p>
            )}
            <div className="demo-results" aria-label="Crawler Demo Results">
              {modelResults.length ? (
                <div className="demo-model-grid">
                  {modelResults.map((m, idx) => (
                    <div className="demo-model-card" key={`${m.profileUrl || m.imageUrl || m.name || 'model'}-${idx}`}>
                      <div className="demo-model-card__img">
                        {m.imageUrl ? (
                          <img src={m.imageUrl} alt={m.name || 'Model'} />
                        ) : (
                          <div className="demo-model-card__img--placeholder" />
                        )}
                      </div>
                      <div className="demo-model-card__meta">
                        <div className="demo-model-card__name">{m.name || t.modeltool.unknown}</div>
                        {m.data && Object.keys(m.data).length ? (
                          <div className="demo-model-card__data">
                            {m.data.heightCm ? <span>{t.modeltool.height} {m.data.heightCm}cm</span> : null}
                            {m.data.bustCm ? <span>{t.modeltool.bust} {m.data.bustCm}cm</span> : null}
                            {m.data.chestCm ? <span>{t.modeltool.chest} {m.data.chestCm}cm</span> : null}
                            {m.data.waistCm ? <span>{t.modeltool.waist} {m.data.waistCm}cm</span> : null}
                            {m.data.hipsCm ? <span>{t.modeltool.hips} {m.data.hipsCm}cm</span> : null}
                            {m.data.shoesEu ? <span>{t.modeltool.shoes} {m.data.shoesEu}</span> : null}
                            {m.data.cup ? <span>{t.modeltool.cup} {m.data.cup}</span> : null}
                            {m.data.eyes ? <span>{t.modeltool.eyes} {m.data.eyes}</span> : null}
                            {m.data.hair ? <span>{t.modeltool.hair} {m.data.hair}</span> : null}
                          </div>
                        ) : null}
                        {m.profileUrl ? (
                          <div className="demo-model-card__actions">
                            <a className="demo-model-card__link" href={m.profileUrl} target="_blank" rel="noopener noreferrer">
                              {t.modeltool.openProfile}
                            </a>
                            <button
                              type="button"
                              className="demo-model-card__pdf-button"
                              onClick={() => downloadModelPDF(m)}
                              disabled={downloadingPdf === m.profileUrl}
                            >
                              {downloadingPdf === m.profileUrl ? t.modeltool.pdfCreating : t.modeltool.pdfDownload}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="demo-results__hint">{t.modeltool.hint}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Liste mit allen Einträgen */}
      {allModels.length > 0 && (
        <section className="models-list-section">
          <div className="models-list-section__header">
            <h2>{t.modeltool.allModelsTitle} ({allModels.length})</h2>
          </div>
          <div className="models-list">
            {allModels.map((m, idx) => (
              <div className="models-list-item" key={`all-${m.profileUrl || m.imageUrl || m.name || 'model'}-${idx}`}>
                <div className="models-list-item__img">
                  {m.imageUrl ? (
                    <img src={m.imageUrl} alt={m.name || 'Model'} />
                  ) : (
                    <div className="models-list-item__img--placeholder" />
                  )}
                </div>
                <div className="models-list-item__content">
                  <div className="models-list-item__name">{m.name || t.modeltool.unknown}</div>
                  {m.data && Object.keys(m.data).length > 0 ? (
                    <div className="models-list-item__data">
                      {m.data.heightCm && <span>{t.modeltool.height}: {m.data.heightCm}cm</span>}
                      {m.data.bustCm && <span>{t.modeltool.bust}: {m.data.bustCm}cm</span>}
                      {m.data.chestCm && <span>{t.modeltool.chest}: {m.data.chestCm}cm</span>}
                      {m.data.waistCm && <span>{t.modeltool.waist}: {m.data.waistCm}cm</span>}
                      {m.data.hipsCm && <span>{t.modeltool.hips}: {m.data.hipsCm}cm</span>}
                      {m.data.shoesEu && <span>{t.modeltool.shoes}: {m.data.shoesEu}</span>}
                      {m.data.cup && <span>{t.modeltool.cup}: {m.data.cup}</span>}
                      {m.data.eyes && <span>{t.modeltool.eyes}: {m.data.eyes}</span>}
                      {m.data.hair && <span>{t.modeltool.hair}: {m.data.hair}</span>}
                    </div>
                  ) : null}
                  <div className="models-list-item__actions">
                    {m.profileUrl && (
                      <>
                        <a className="models-list-item__link" href={m.profileUrl} target="_blank" rel="noopener noreferrer">
                          {t.modeltool.openProfile}
                        </a>
                        <button
                          type="button"
                          className="models-list-item__pdf-button"
                          onClick={() => downloadModelPDF(m)}
                          disabled={downloadingPdf === m.profileUrl}
                        >
                          {downloadingPdf === m.profileUrl ? t.modeltool.pdfCreating : t.modeltool.pdfDownload}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {selectedImage && (
        <div className="modal" onClick={() => setSelectedImage(null)}>
          <div className="modal-content">
            <img src={selectedImage.src} alt={selectedImage.alt} />
          </div>
        </div>
      )}
    </div>
  );
}

export default ModelToolMBS; 
